import { useEffect, useRef, useState } from 'react'
import {
  chordText, diatonicQuality, noteName, qualitiesFor, quality,
} from '../lib/chords'
import { Chord } from './ChordChart'

/**
 * Pick a chord: a root, then a quality, then — behind Advanced — a bass note.
 *
 * The quality list is a scroll wheel rather than a grid because the advanced
 * vocabulary is twenty deep and a grid of twenty chord suffixes is a wall.
 * A wheel has one thing selected at a time, which is what a chord is.
 *
 * Built on scroll-snap rather than a drag library: momentum, rubber-banding and
 * touch handling all come from the platform for free, and — the part a custom
 * wheel usually loses — every option stays a real <button>, so it is reachable
 * by tab and readable by a screen reader. Scrolling and clicking both select;
 * neither is the "real" input.
 */

const ITEM_H = 44

function Wheel({ items, value, onChange, label }) {
  const ref = useRef(null)
  const settling = useRef(null)
  const [live, setLive] = useState(value)

  // Bring the selected item under the marker when opened or changed from
  // outside. Instant, not smooth: this is positioning, not a transition.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const i = items.findIndex((it) => it.id === value)
    if (i < 0) return
    setLive(value)
    el.scrollTo({ top: i * ITEM_H, behavior: 'auto' })
  }, [value, items])

  /**
   * Scroll reports continuously; the commit has to wait for it to stop or every
   * flick writes a dozen chords. The nearest item is highlighted live so the
   * wheel feels responsive, but only the resting one is committed.
   */
  const onScroll = () => {
    const el = ref.current
    if (!el) return
    const i = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ITEM_H)))
    if (items[i] && items[i].id !== live) setLive(items[i].id)
    clearTimeout(settling.current)
    settling.current = setTimeout(() => {
      if (items[i] && items[i].id !== value) onChange(items[i].id)
    }, 120)
  }

  useEffect(() => () => clearTimeout(settling.current), [])

  return (
    <div className="wheel-wrap">
      <div className="wheel-marker" aria-hidden="true" />
      <div className="wheel" ref={ref} onScroll={onScroll} role="listbox" aria-label={label}>
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            role="option"
            aria-selected={it.id === value}
            className={`wheel-item${it.id === live ? ' live' : ''}`}
            onClick={() => onChange(it.id)}
          >
            <span className="wheel-face">{it.face}</span>
            {it.hint && <span className="wheel-hint">{it.hint}</span>}
          </button>
        ))}
      </div>
    </div>
  )
}

const DEGREES = [1, 2, 3, 4, 5, 6, 7]

export default function ChordPicker({ value, songKey, mode, onChange, onRemove, onClose }) {
  const [advanced, setAdvanced] = useState(
    () => localStorage.getItem('beseter.chords.advanced') === '1'
  )
  const ch = value ?? { d: 1, a: 0, q: 'maj', b: null, ba: 0 }

  useEffect(() => {
    localStorage.setItem('beseter.chords.advanced', advanced ? '1' : '0')
  }, [advanced])

  const set = (patch) => onChange({ ...ch, ...patch })

  // Turning Advanced off while sitting on an advanced quality would leave a
  // chord the wheel can't show. Fall back to the nearest basic one.
  const qualities = qualitiesFor(advanced)
  useEffect(() => {
    if (!qualities.some((q) => q.id === ch.q)) set({ q: diatonicQuality(ch.d) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanced])

  const rootItems = DEGREES.map((d) => ({
    id: String(d),
    face: mode === 'numbers' ? String(d) : noteName(d, 0, songKey),
    hint: mode === 'numbers' ? noteName(d, 0, songKey) : String(d),
  }))

  const qualityItems = qualities.map((q) => ({
    id: q.id,
    face: (mode === 'numbers' ? q.n : q.c) || (mode === 'numbers' ? 'plain' : 'major'),
    hint: q.label,
  }))

  const bassItems = [
    { id: 'none', face: '—', hint: 'root in the bass' },
    ...DEGREES.map((d) => ({
      id: String(d),
      face: mode === 'numbers' ? `/${d}` : `/${noteName(d, 0, songKey)}`,
      hint: 'inversion',
    })),
  ]

  return (
    <div className="picker">
      <div className="picker-head">
        <div className="picker-preview">
          <b><Chord ch={ch} mode={mode} songKey={songKey} /></b>
          <small>
            {quality(ch.q).label}
            {mode === 'numbers' && ` · ${chordText(ch, { mode: 'chords', key: songKey })} in ${songKey}`}
            {mode === 'chords' && ` · ${chordText(ch, { mode: 'numbers', key: songKey })}`}
          </small>
        </div>
        <button className="iconbtn" onClick={onClose} aria-label="Close chord picker">Done</button>
      </div>

      <div className="picker-wheels">
        <div className="picker-col">
          <span className="picker-cap">Root</span>
          <Wheel items={rootItems} value={String(ch.d)} label="Root"
                 onChange={(id) => set({ d: Number(id), q: diatonicQuality(Number(id)) })} />
        </div>
        <div className="picker-col picker-col-wide">
          <span className="picker-cap">Quality</span>
          <Wheel items={qualityItems} value={ch.q} label="Quality"
                 onChange={(id) => set({ q: id })} />
        </div>
        {advanced && (
          <div className="picker-col">
            <span className="picker-cap">Bass</span>
            <Wheel items={bassItems} value={ch.b == null ? 'none' : String(ch.b)} label="Bass note"
                   onChange={(id) => set({ b: id === 'none' ? null : Number(id), ba: 0 })} />
          </div>
        )}
      </div>

      {/* Sharps and flats are the borrowed chords — a b7 or a b3. Common enough
          to need, rare enough not to earn a fourth wheel. */}
      <div className="picker-acc">
        {[-1, 0, 1].map((a) => (
          <button key={a} className={`chip${ch.a === a ? ' on' : ''}`}
                  onClick={() => set({ a })}>
            {a === -1 ? '♭ flat' : a === 1 ? '♯ sharp' : 'natural'}
          </button>
        ))}
      </div>

      <div className="picker-foot">
        <label className="picker-adv">
          <input type="checkbox" checked={advanced}
                 onChange={(e) => setAdvanced(e.target.checked)} />
          <span>
            Advanced
            <small>9ths, 11ths, sus, inversions</small>
          </span>
        </label>
        {onRemove && (
          <button className="btn-plain" style={{ color: 'var(--danger)' }} onClick={onRemove}>
            Remove chord
          </button>
        )}
      </div>
    </div>
  )
}
